/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Vladimir Ganturin <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {ChangeDetectionStrategy, Component, HostListener, OnInit} from '@angular/core';
import {FormlyModule} from '@ngx-formly/core'
import { NgClass } from '@angular/common';
import {BaseFieldType} from '../base-field-type'
import {FormatSizePipe} from '../../pipes/format-size'
import {FormsModule} from '@angular/forms'
import {TuiIcon} from '@taiga-ui/core'
import {translations} from '../../translations.ru'
import {takeUntil} from 'rxjs/operators'

@Component({
  templateUrl: './file-upload.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormlyModule,
    NgClass,
    FormatSizePipe,
    FormsModule,
    TuiIcon
],
  providers: [FormatSizePipe]
})
export class FileUploadComponent extends BaseFieldType implements OnInit {
  file: File = null;
  allFiles: File[] = [];
  hasDropZoneOver = false;

  ngOnInit(): void {
    // Выбранный файл живёт в локальных this.file/this.allFiles (см. fileName/fileSize),
    // отдельно от formControl.value. При внешнем сбросе формы (resetModel/resetForm после
    // успешного create) обнуляется только контрол — локальное состояние оставалось, и файл
    // продолжал отображаться. Слушаем обнуление контрола и чистим локальное состояние.
    this.formControl.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        if (!this.hasValue && (this.file || this.allFiles.length > 0)) {
          this.file = null;
          this.allFiles = [];
          this.cdr.detectChanges();
        }
      });
  }

  get value() {
    return this.formControl.value;
  }

  set value(value: any) {
    this.formControl.patchValue(value);
  }

  get fileType() {
    return this.file?.type;
  }

  get fileName() {
    return this.file?.name ? this.file.name : (this.field.props.fileOptions?.name || translations.chooseFile2);
  }

  get fileSize(): number {
    return this.allFiles.reduce((sum, file) => sum + file.size, 0);
  }

  get maxFileSize(): number {
    return this.field.props?.fileOptions?.maxFileSize;
  }

  get allowedMimeTypes(): string[] {
    return this.field.props?.fileOptions?.allowedMimeType || [];
  }

  get accept(): string {
    return this.field.props?.fileOptions?.accept || '';
  }

  get isBig()  {
    return this.field.props?.fileOptions?.size == 'l';
  }

  get isMultiple(): boolean {
    return this.field.props?.fileOptions?.multiple || false;
  }

  onFileSelect($event: Event): void {
    const input = $event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.setFiles(Array.from(input.files));
    }
    // Обнуляем нативный input, иначе повторный выбор того же файла
    // не вызывает change (браузер видит, что значение не изменилось).
    input.value = '';
  }

  setFiles(files: File[]): void {
    this.allFiles = files;
    this.file = this.allFiles[0];

    this.validate();

    if (!this.hasErrors) {
      if (this.isMultiple) {
        this.formControl.setValue(this.allFiles);
      } else {
        this.formControl.setValue(this.file);
      }
    }

    this.hasDropZoneOver = false;
    this.cdr.detectChanges();
  }

  get hasErrors(): boolean {
    return this.formControl.touched && !!this.formControl.errors;
  }

  validate(): void {
    this.formControl.setErrors(null);

    if (this.fileSize > this.maxFileSize) {
      this.formControl.setErrors({
        maxFileSizeExceed: {actualSize: this.fileSize, maxSize: this.maxFileSize},
      });
    }

    if (this.allowedMimeTypes.length > 0) {
      const invalidFiles = this.allFiles?.filter(file =>
        !this.allowedMimeTypes.includes(file.type) && file.type !== ''
      );

      if (invalidFiles.length > 0) {
        this.formControl.setErrors({
          mimeTypeNotAllowed: {actualMime: invalidFiles[0].type || '', allowed: this.allowedMimeTypes},
        });
      }
    }
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.hasDropZoneOver = true;
    this.cdr.detectChanges();
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.hasDropZoneOver = false;
    this.cdr.detectChanges();
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.hasDropZoneOver = false;

    if (event.dataTransfer?.files) {
      const files = Array.from(event.dataTransfer.files);
      this.setFiles(files);
    }
  }

}
